import React from 'react';
import {
  JobsiteUpdateInput,
  useJobsiteUpdateMutation,
} from 'apollo/generated/client-operations';
import { Tab } from '@odin-labs/components';
import { AlertService } from 'components/alertService';
import { FormOnSubmit } from 'components/form';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import { useAvailableJobsites } from 'graphql/client/useAvailableJobsites';
import { useDeferredFormSubmission, useIsMounted } from 'utils';
import { getGraphQLError } from 'utils/error';
import type { DeferredFormSubmission } from 'utils/useDeferredFormSubmission';

type SubmitArgs<TFields> = Parameters<FormOnSubmit<TFields>>;

/**
 * Each site's last jobsite update. The API rewrites a site's whole `modules`
 * from what it read, so two overlapping updates (say Features, left saving
 * with "Leave anyway", then Geofencing) would let the later one drop the
 * other's changes. A site's updates therefore run one after another, even
 * once the tab that started one has gone.
 */
const siteUpdateQueues = new Map<string, Promise<unknown>>();

/** Runs `update` once the site's earlier updates have settled. */
const queueSiteUpdate = <T>(
  jobsiteId: string,
  update: () => Promise<T>,
): Promise<T> => {
  const earlier = siteUpdateQueues.get(jobsiteId) ?? Promise.resolve();
  const next = earlier.then(update);
  const settled = next.then(
    (): undefined => undefined,
    (): undefined => undefined,
  );
  siteUpdateQueues.set(jobsiteId, settled);
  void settled.then(() => {
    if (siteUpdateQueues.get(jobsiteId) === settled)
      siteUpdateQueues.delete(jobsiteId);
  });
  return next;
};

export type JobsiteConfigTabFormOptions<TFields> = Pick<
  JobsiteConfigTabProps,
  'onTabApiChange' | 'onIsDirtyChange'
> & {
  /** The tab, whose label its success toast names. */
  tab: Pick<Tab, 'text'>;
  /** Builds the jobsite update from what the form submitted. */
  getInput: (
    data: SubmitArgs<TFields>[0],
    dirtyFields: SubmitArgs<TFields>[2],
  ) => JobsiteUpdateInput | Promise<JobsiteUpdateInput>;
  /** Reloads the tab's data once a save has succeeded. */
  refetch: () => unknown;
  /**
   * Lets the tab show a failed save its own way, e.g. as an error on a field.
   * Returns true when the tab has shown the error, so the hook skips its
   * generic error toast; false leaves the error to that toast. Only called
   * while the tab is still mounted.
   */
  onError?: (error: unknown, formApi: SubmitArgs<TFields>[3]) => boolean;
};

/** The tab's form ref, and the onSubmit that saves it. */
export type JobsiteConfigTabForm<TFields> = Pick<
  DeferredFormSubmission<TFields>,
  'formRef'
> & { onSubmit: FormOnSubmit<TFields> };

/**
 * Wires a jobsite settings tab's form to the settings header and saves it:
 * the tab owns its form and hands the container a `submit` for Save Changes
 * and the leave-page prompt, withdrawn when the tab unmounts. Tabs without a
 * form don't use this, so Save never reaches a tab that has nothing to submit.
 */
export function useJobsiteConfigTabForm<TFields>({
  onTabApiChange,
  onIsDirtyChange,
  tab,
  getInput,
  refetch,
  onError,
}: JobsiteConfigTabFormOptions<TFields>): JobsiteConfigTabForm<TFields> {
  const { formRef, deferredSubmission, submitForm } =
    useDeferredFormSubmission<TFields>();
  const isMounted = useIsMounted();
  const [jobsiteUpdate] = useJobsiteUpdateMutation();
  // This tab's own save, so a save left running on a tab the user has moved
  // away from doesn't hold up or misdescribe the next tab.
  const [isSaving, setIsSaving] = React.useState(false);
  React.useEffect(() => {
    onTabApiChange?.({ isSaving });
  }, [onTabApiChange, isSaving]);

  // Each render's `submitForm` waits on that render's `deferredSubmission`, so
  // the container gets one stable `submit` that calls the latest committed one,
  // the one whose `deferredSubmission` the form's `onSubmit` resolves.
  const submitFormRef = React.useRef(submitForm);
  React.useLayoutEffect(() => {
    submitFormRef.current = submitForm;
  });

  React.useEffect(() => {
    onTabApiChange?.({
      // Nothing to submit while the tab is still loading its form.
      submit: (): Promise<boolean> =>
        formRef.current ? submitFormRef.current() : Promise.resolve(false),
    });
    return (): void => onTabApiChange?.({ submit: undefined, isSaving: false });
  }, [onTabApiChange, formRef]);

  const onSubmit: FormOnSubmit<TFields> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isSaving) {
      AlertService.alert('warn', 'Saving in progress!', '');
      return;
    }
    setIsSaving(true);

    try {
      const input = await getInput(data, dirtyFields);
      await queueSiteUpdate(input.jobsiteId, () =>
        jobsiteUpdate({ variables: { input } }),
      );

      setIsSaving(false);
      AlertService.alert(
        'success',
        'Success',
        `${tab.text} configuration successfully updated`,
      );
      // Only a tab still shown clears the unsaved changes: after "Leave
      // anyway", they are another tab's.
      if (isMounted()) {
        onIsDirtyChange?.(false);
        refetch();
      }
      useAvailableJobsites.resetCacheOnNextCall();
      deferredSubmission.resolve(true);
    } catch (error) {
      event.preventDefault();
      setIsSaving(false);
      deferredSubmission.reject(error);
      // After "Leave anyway" nobody sees the tab's form, so the toast is the
      // only place left to report the failure.
      if (isMounted() && onError?.(error, formApi)) return;
      // Named like the success toast: by now it may show over another tab.
      AlertService.alert(
        'danger',
        'Something went wrong!',
        [`${tab.text} configuration was not updated.`, getGraphQLError(error)]
          .filter(Boolean)
          .join(' '),
      );
    }
  };

  return { formRef, onSubmit };
}
